/**
 * useFormProgress - Centralized application form progress calculator
 *
 * Calculates global progress across Page1 + Page2 + Page3 + Page4.
 * PageA (Appendix) and Page5/14 (Application Summary) are info-only
 * and do NOT contribute to progress.
 *
 * Page1 primary fields (15): location, advertisedOn, advertisedIn, advertisementSource,
 *   nameInBlocks, fatherOrHusbandName, dateOfBirth, dobProofFile, qualification,
 *   qualificationProofFile, currentAddress, permanentAddress, addressProofFile,
 *   personalMobile, emailId
 * Page1 partner fields (12 each): photo, nameInBlocks, fatherOrHusbandName, dateOfBirth,
 *   dobProofFile, qualification, qualificationProofFile, currentAddress, permanentAddress,
 *   addressProofFile, personalMobile, emailId
 *
 * Page2 primary fields (4): aadhaarNumber, panNumberStatutory, gstNumber, identityProofCheckbox
 * Page2 partner fields: overlap with Page1 (nameInBlocks, fatherOrHusbandName, dateOfBirth,
 *   qualification) — already counted, no duplicates
 *
 * Page3 primary fields (3): declarationPlace, declarationDate, signatureFile
 * Page3 partner fields (1 each): signatureFile
 *
 * Page4 document fields (21): doc1File..doc5File, doc6aFile, doc6bFile, doc7File..doc20File
 *   (application-level, not per-partner)
 *
 * Total primary: 22 (Page1+2+3) + 21 (Page4) = 43 fields
 * Total per partner: 13 unique fields (12 from Page1 + 1 signatureFile from Page3)
 */

import { useMemo } from 'react';

interface PartnerDetail {
  photo?: any;
  nameInBlocks?: string;
  fatherOrHusbandName?: string;
  dateOfBirth?: string;
  dobProofFile?: any;
  qualification?: string;
  qualificationProofFile?: any;
  currentAddress?: string;
  permanentAddress?: string;
  addressProofFile?: any;
  personalMobile?: string;
  emailId?: string;
  signatureFile?: any;
  [key: string]: any;
}

interface FormData {
  // Page 1 primary fields
  location?: string;
  advertisedOn?: string;
  advertisedIn?: string;
  advertisementSource?: string;
  nameInBlocks?: string;
  fatherOrHusbandName?: string;
  dateOfBirth?: string;
  dobProofFile?: any;
  qualification?: string;
  qualificationProofFile?: any;
  currentAddress?: string;
  permanentAddress?: string;
  addressProofFile?: any;
  personalMobile?: string;
  emailId?: string;
  // Page 2 primary fields
  aadhaarNumber?: string;
  panNumberStatutory?: string;
  gstNumber?: string;
  identityProofVoterId?: boolean;
  identityProofPassport?: boolean;
  identityProofDrivingLicense?: boolean;
  // Page 3 primary fields
  declarationPlace?: string;
  declarationDate?: string;
  signatureFile?: any;
  // Page 4 document fields (21)
  doc1File?: any;
  doc2File?: any;
  doc3File?: any;
  doc4File?: any;
  doc5File?: any;
  doc6aFile?: any;
  doc6bFile?: any;
  doc7File?: any;
  doc8File?: any;
  doc9File?: any;
  doc10File?: any;
  doc11File?: any;
  doc12File?: any;
  doc13File?: any;
  doc14File?: any;
  doc15File?: any;
  doc16File?: any;
  doc17File?: any;
  doc18File?: any;
  doc19File?: any;
  doc20File?: any;
  // Partners
  partnerDirectorDetails?: PartnerDetail[];
  [key: string]: any;
}

const isFilledStr = (val: any): boolean => typeof val === 'string' && val.trim() !== '';
const isFilledFile = (val: any): boolean => !!val;

// Page 4 document field keys
const DOC_FIELDS = [
  'doc1File', 'doc2File', 'doc3File', 'doc4File', 'doc5File',
  'doc6aFile', 'doc6bFile',
  'doc7File', 'doc8File', 'doc9File', 'doc10File', 'doc11File',
  'doc12File', 'doc13File', 'doc14File', 'doc15File', 'doc16File',
  'doc17File', 'doc18File', 'doc19File', 'doc20File',
] as const;

export function useFormProgress(formData: FormData): number {
  return useMemo(() => {
    let filledFields = 0;

    // ========== PAGE 1 PRIMARY (15 fields) ==========
    if (isFilledStr(formData.location)) filledFields++;
    if (isFilledStr(formData.advertisedOn)) filledFields++;
    if (isFilledStr(formData.advertisedIn)) filledFields++;
    if (isFilledStr(formData.advertisementSource)) filledFields++;
    if (isFilledStr(formData.nameInBlocks)) filledFields++;
    if (isFilledStr(formData.fatherOrHusbandName)) filledFields++;
    if (isFilledStr(formData.dateOfBirth)) filledFields++;
    if (isFilledFile(formData.dobProofFile)) filledFields++;
    if (isFilledStr(formData.qualification)) filledFields++;
    if (isFilledFile(formData.qualificationProofFile)) filledFields++;
    if (isFilledStr(formData.currentAddress)) filledFields++;
    if (isFilledStr(formData.permanentAddress)) filledFields++;
    if (isFilledFile(formData.addressProofFile)) filledFields++;
    if (isFilledStr(formData.personalMobile)) filledFields++;
    if (isFilledStr(formData.emailId)) filledFields++;

    // ========== PAGE 2 PRIMARY (4 fields) ==========
    if (isFilledStr(formData.aadhaarNumber)) filledFields++;
    if (isFilledStr(formData.panNumberStatutory)) filledFields++;
    if (isFilledStr(formData.gstNumber)) filledFields++;
    if (formData.identityProofVoterId || formData.identityProofPassport || formData.identityProofDrivingLicense) filledFields++;

    // ========== PAGE 3 PRIMARY (3 fields) ==========
    if (isFilledStr(formData.declarationPlace)) filledFields++;
    if (isFilledStr(formData.declarationDate)) filledFields++;
    if (isFilledFile(formData.signatureFile)) filledFields++;

    // ========== PAGE 4 DOCUMENTS (21 fields) ==========
    for (const docKey of DOC_FIELDS) {
      if (isFilledFile(formData[docKey])) filledFields++;
    }

    // ========== PARTNER FIELDS (13 unique per partner) ==========
    const partners = formData.partnerDirectorDetails || [];

    partners.forEach((partner: PartnerDetail) => {
      // Page 1 partner fields (12)
      if (isFilledFile(partner.photo)) filledFields++;
      if (isFilledStr(partner.nameInBlocks)) filledFields++;
      if (isFilledStr(partner.fatherOrHusbandName)) filledFields++;
      if (isFilledStr(partner.dateOfBirth)) filledFields++;
      if (isFilledFile(partner.dobProofFile)) filledFields++;
      if (isFilledStr(partner.qualification)) filledFields++;
      if (isFilledFile(partner.qualificationProofFile)) filledFields++;
      if (isFilledStr(partner.currentAddress)) filledFields++;
      if (isFilledStr(partner.permanentAddress)) filledFields++;
      if (isFilledFile(partner.addressProofFile)) filledFields++;
      if (isFilledStr(partner.personalMobile)) filledFields++;
      if (isFilledStr(partner.emailId)) filledFields++;
      // Page 2 partner fields (nameInBlocks, fatherOrHusbandName, dateOfBirth, qualification)
      // overlap with Page 1 partner fields — already counted above
      // Page 3 partner fields (1)
      if (isFilledFile(partner.signatureFile)) filledFields++;
    });

    // Total: 43 primary + (13 unique partner fields × number of partners)
    // Primary = 15 (Page1) + 4 (Page2) + 3 (Page3) + 21 (Page4) = 43
    // Per partner = 12 (Page1) + 0 unique from Page2 + 1 (Page3 signatureFile) = 13
    const PRIMARY_FIELDS = 43;
    const UNIQUE_PARTNER_FIELDS = 13;
    const totalFields = PRIMARY_FIELDS + (partners.length * UNIQUE_PARTNER_FIELDS);

    return totalFields > 0 ? (filledFields / totalFields) * 100 : 0;
  }, [formData]);
}

export default useFormProgress;
